import React, { memo, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { useTranslation } from 'next-i18next';
import { differenceInDays, format, isAfter, isBefore } from 'date-fns';

import Icon, { IconName } from 'components/Icon';
import { Chip, ApplicationDeadline, OpportunitySalary } from 'components/ComponentV2';
import Tooltip from 'components/Tooltip';
import { useNotification } from 'hooks/useNotification';
import { useResponsive } from 'hooks/useResponsive';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { TouchpointType } from 'lib/models/touchpoint';
import { JobTouchpointable } from 'lib/models/job';
import { InternshipTouchpointable } from 'lib/models/internship';
import { EventTouchpointable } from 'lib/models/event';
import { useOpportunity } from 'lib/providers/OpportunityProvider';

import styles from './OpportunityLabels.module.scss';

type OpportunityLabelsProps = {
  className?: string;
};

export const OpportunityLabels: React.FC<OpportunityLabelsProps> = memo(({ className = '' }: OpportunityLabelsProps) => {
  const { opportunity, isApplicationClosed, isExpired, isEventEnded, isSearchPage } = useOpportunity();
  const [tc] = useTranslation('common');
  const notificationInstance = useNotification();
  const screens = useResponsive();
  const {
    touchpointable_type,
    touchpointable: touchpointableResponse,
    area_of_responsibilities,
    cities: citiesResponse,
    remote_countries,
    is_online,
    online_link,
    pay_currency,
    application_deadline,
    place: placeResponse,
    apply_url,
    is_eligible_for_auto_apply,
    kind,
  } = opportunity;

  const {
    isJob,
    isInternship,
    isEvent,
    isEventLive,
    touchpointIcon,
    workTypeLabel,
    minimumSalary,
    maximumSalary,
    eventCategories,
    opportunityStartDate,
    opportunityEndDate,
  } = useMemo(() => {
    let touchpointIcon: IconName | null = null,
      isFullTime = null,
      minimumSalary = null,
      maximumSalary = null,
      isJob = false,
      isInternship = false,
      isEvent = false,
      isEventLive = false,
      opportunityStartDate = null,
      opportunityEndDate = null,
      eventCategories: Array<string> = [];
    switch (touchpointable_type) {
      case TouchpointType.Job: {
        const { full_time, minimum_salary, maximum_salary, start_date } = parseResponse(touchpointableResponse as JobTouchpointable);
        isJob = true;
        touchpointIcon = 'icon_briefcase';
        isFullTime = full_time;
        minimumSalary = minimum_salary;
        maximumSalary = maximum_salary;
        opportunityStartDate = start_date ? format(new Date(start_date), 'MMM dd') : null;
        break;
      }
      case TouchpointType.Internship: {
        const { full_time, minimum_salary, maximum_salary, start_date } = parseResponse(touchpointableResponse as InternshipTouchpointable);
        isInternship = true;
        touchpointIcon = 'icon_spaceship';
        isFullTime = full_time;
        minimumSalary = minimum_salary;
        maximumSalary = maximum_salary;
        opportunityStartDate = start_date ? format(new Date(start_date), 'MMM dd') : null;
        break;
      }
      case TouchpointType.Event: {
        const { event_categories, start_date, end_date } = parseResponse(touchpointableResponse as EventTouchpointable);
        const categories = parseArrayResponse(event_categories);
        eventCategories = categories.map((category) => category.name);
        isEvent = true;
        isEventLive = !isExpired && !isEventEnded && isAfter(new Date(), new Date(start_date)) && isBefore(new Date(), new Date(end_date));
        touchpointIcon = 'icon_party';
        opportunityStartDate = start_date ? format(new Date(start_date), 'MMM dd, hh:mm a') : null;
        opportunityEndDate = end_date ? format(new Date(end_date), 'MMM dd, hh:mm a') : null;
        break;
      }
      default:
        break;
    }

    return {
      isJob,
      isInternship,
      isEvent,
      isEventLive,
      touchpointIcon,
      eventCategories,
      workTypeLabel: isFullTime === null ? null : isFullTime ? tc('full-time') : tc('part-time'),
      minimumSalary,
      maximumSalary,
      opportunityStartDate,
      opportunityEndDate,
    };
  }, [touchpointable_type, isExpired, isEventEnded, touchpointableResponse, tc]);

  const roles = useMemo(() => {
    return parseArrayResponse(area_of_responsibilities).map((role) => role.name);
  }, [area_of_responsibilities]);

  const { locations, hasRemote = false } = useMemo(() => {
    if (isJob || isInternship) {
      const cities = parseArrayResponse(citiesResponse).map((city) => city.name);
      const remoteCountries = parseArrayResponse(remote_countries).map((country) => country.name);
      return {
        locations: cities.length ? cities : remoteCountries,
        hasRemote: is_online,
      };
    } else if (isEvent && !is_online) {
      const place = parseResponse(placeResponse);
      if (!place) return { locations: [] };
      const { city, country, address } = place;
      const add = city && country ? `${city}, ${country}` : city || country ? city || country : '';
      return { locations: address ? [address] : [add] };
    } else {
      return { locations: [] };
    }
  }, [isJob, isInternship, isEvent, placeResponse, citiesResponse, remote_countries, is_online]);

  const eventOnlineLink = useMemo(() => {
    if (!isEvent || !is_online || !online_link) return null;
    return online_link;
  }, [isEvent, is_online, online_link]);

  const applicationDeadline = useMemo(() => {
    if (!application_deadline || isExpired || isEventEnded) return null;
    const applicationDeadlineTimestamp = new Date(application_deadline);
    const now = new Date();
    const diff = differenceInDays(applicationDeadlineTimestamp, now);
    if (diff >= 30) return null;
    return <ApplicationDeadline date={application_deadline} />;
  }, [application_deadline, isExpired, isEventEnded]);

  const formattedLocationTooltip = useMemo(() => {
    return locations.length > 1
      ? `${tc('bold-location', { locations: locations.slice(0, -1).join(', ') })} ${tc('and')} ${tc('bold-location', {
          locations: locations.slice(-1),
        })}`
      : locations[0];
  }, [locations]);

  const handleCopyEventLink = useCallback(async () => {
    if (!eventOnlineLink) return;

    try {
      await navigator.clipboard.writeText(eventOnlineLink);
      notificationInstance.acknowledge({
        message: tc('opportunity.link-copied'),
      });
    } catch (error) {
      console.error(error);
    }
  }, [eventOnlineLink]);

  const isEasyRegister = isEvent && !isEventEnded && !isExpired && !isApplicationClosed && !apply_url;
  const isQuickApply = (isJob || isInternship) && !isExpired && !isApplicationClosed && is_eligible_for_auto_apply;

  const tooltipPosition = screens.sm ? 'right' : 'top';

  return (
    <div className={cx(styles.container, className)}>
      <ul>
        <li>
          {touchpointIcon && (
            <Tooltip content={tc('opportunity.basic-info')} color="dark" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName={touchpointIcon as IconName} size="auto" />
            </Tooltip>
          )}
          <div className={styles.chips}>
            <Chip>{opportunity.touchpointable_type}</Chip>
            {isEvent && eventCategories.map((eventCategory) => <Chip key={eventCategory}>{eventCategory}</Chip>)}
            {workTypeLabel && <Chip>{workTypeLabel}</Chip>}
            {(isJob || isInternship) && opportunityStartDate && <Chip>{tc('opportunity.starts-on-date', { start_date: opportunityStartDate })}</Chip>}
            {(isJob || isInternship) && kind && <Chip className={styles['experience-level']}>{kind}</Chip>}
          </div>
        </li>
        {isEvent && opportunityStartDate && (
          <li>
            <Tooltip content={tc('opportunity.date-and-time')} color="dark" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName="icon_calendar" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              <Chip>{tc('opportunity.from-date', { start_date: opportunityStartDate })}</Chip>
              {opportunityEndDate && <Chip>{tc('opportunity.to-date', { end_date: opportunityEndDate })}</Chip>}
            </div>
          </li>
        )}
        {(isJob || isInternship) && roles.length > 0 && (
          <li>
            <Tooltip content={tc('opportunity.role')} color="dark" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName="icon_badge" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              {roles.map((role) => (
                <Chip key={role}>{role}</Chip>
              ))}
            </div>
          </li>
        )}
        {(minimumSalary !== null || maximumSalary !== null) && (
          <li>
            <Tooltip content={tc('opportunity.salary')} color="dark" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName="icon_coins" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              <OpportunitySalary minimumSalary={minimumSalary} maximumSalary={maximumSalary} payCurrency={pay_currency} />
            </div>
          </li>
        )}
        {locations.length > 0 && (
          <li>
            <Tooltip content={tc('opportunity.location')} color="dark" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName="icon_pin" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              {locations.length > 0 && (
                <Tooltip
                  containerClassName={styles['location-tooltip']}
                  hasWhitespaceNoWrap={false}
                  disabled={locations.length === 1}
                  isV2Design
                  content={
                    <p
                      dangerouslySetInnerHTML={{
                        __html: tc(isEvent ? 'location-tooltip-content-event' : 'location-tooltip-content-job', {
                          location: formattedLocationTooltip,
                        }),
                      }}
                    />
                  }
                  color="dark"
                  position={'top'}
                  xAlign={screens.sm || isSearchPage ? 'left' : 'center'}
                >
                  <Chip className={styles['location-chip']}>
                    {tc('location-chip', { location: locations[0], count: locations.length, remaining_count: locations.length - 1 })}
                  </Chip>
                </Tooltip>
              )}
              {hasRemote && <Chip className={styles['location-chip']}>{tc('remote')}</Chip>}
            </div>
          </li>
        )}
        {eventOnlineLink && (
          <li>
            <Tooltip content={tc('opportunity.meeting-link')} color="dark" xAlign="center" position={tooltipPosition} showTippy>
              <Icon className={styles.icon} iconName="icon_link" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              <Chip className={styles['location-chip']} type="interactive" onClick={handleCopyEventLink}>
                {eventOnlineLink}
              </Chip>
            </div>
          </li>
        )}
        {(applicationDeadline || isEventLive || isQuickApply || isEasyRegister) && (
          <li>
            <Tooltip
              content={isEvent ? tc('opportunity.registration') : tc('opportunity.application')}
              color="dark"
              position={tooltipPosition}
              showTippy
            >
              <Icon className={styles.icon} iconName="icon_send-message" size="auto" />
            </Tooltip>
            <div className={styles.chips}>
              {isEasyRegister && <Chip type="primary">{tc('opportunity.easy-register')}</Chip>}
              {isQuickApply && <Chip type="primary">{tc('opportunity.quick-apply')}</Chip>}
              {isEventLive ? (
                <Chip className={styles['live-now']}>
                  <Icon iconName="icon_arrow-forward" size="small" />
                  {tc('opportunity.live-now')}
                </Chip>
              ) : (
                applicationDeadline
              )}
            </div>
          </li>
        )}
      </ul>
    </div>
  );
});

OpportunityLabels.displayName = 'OpportunityLabels';
